import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Gauge, Plus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { description: '', equipment: '', characteristic: '', unit: '', is_counter: false, status: 'Active' };

const MeasuringPoints = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('measuring_point');
  const [rows, setRows] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [reading, setReading] = useState({ reading_value: '', note: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/pm/measuring-points`, { withCredentials: true }); setRows(res.data); }
    catch (e) { toast.error('Failed to load measuring points'); }
    finally { setLoading(false); }
  }, []);
  const loadEquipment = useCallback(async () => {
    try { const res = await axios.get(`${API}/pm/equipment`, { withCredentials: true }); setEquipment(res.data); }
    catch (e) { /* non-blocking */ }
  }, []);

  useEffect(() => { load(); loadEquipment(); }, [load, loadEquipment]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/pm/measuring-points`, {
        ...form, equipment: form.equipment || null, unit: form.unit || null,
      }, { withCredentials: true });
      toast.success('Measuring point created'); setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
    finally { setSaving(false); }
  };

  const addReading = async () => {
    if (reading.reading_value === '') return toast.error('Enter a reading value');
    setBusy(true);
    try {
      const res = await axios.post(`${API}/pm/measuring-points/${detail.measuring_point}/readings`, {
        reading_value: parseFloat(reading.reading_value), note: reading.note || null,
      }, { withCredentials: true });
      setDetail(res.data); setReading({ reading_value: '', note: '' }); toast.success('Reading recorded'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to record reading'); }
    finally { setBusy(false); }
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="measuring-points-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-sky-100 text-sky-700"><Gauge className="h-5 w-5" /></div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Measuring Points & Counters</h1>
              <p className="text-gray-600 text-sm mt-0.5">Condition monitoring readings and equipment counters.</p>
            </div>
          </div>
          {writable && <Button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="new-mp-btn"><Plus className="h-4 w-4 mr-2" />New Measuring Point</Button>}
        </div>

        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>MP #</TableHead><TableHead>Description</TableHead><TableHead>Equipment</TableHead>
              <TableHead>Characteristic</TableHead><TableHead>Type</TableHead>
              <TableHead className="text-right">Last Reading</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">Loading...</TableCell></TableRow>
                : rows.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">No measuring points yet.</TableCell></TableRow>
                : rows.map((r) => (
                  <TableRow key={r.measuring_point} data-testid={`mp-row-${r.measuring_point}`}>
                    <TableCell className="font-medium">{r.measuring_point}</TableCell>
                    <TableCell>{r.description}</TableCell>
                    <TableCell>{r.equipment || '—'}</TableCell>
                    <TableCell>{r.characteristic || '—'}</TableCell>
                    <TableCell><Badge variant={r.is_counter ? 'default' : 'secondary'}>{r.is_counter ? 'Counter' : 'Measuring Point'}</Badge></TableCell>
                    <TableCell className="text-right">{r.last_reading ?? '—'} {r.unit || ''}</TableCell>
                    <TableCell><Button size="sm" variant="outline" onClick={() => setDetail(r)} data-testid={`open-mp-${r.measuring_point}`}>Readings</Button></TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Create */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Measuring Point</DialogTitle>
            <DialogDescription>Track a characteristic or a running counter for an asset.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2"><Label>Description *</Label><Input data-testid="mp-description" value={form.description} onChange={(e) => set('description', e.target.value)} /></div>
            <div>
              <Label>Equipment</Label>
              <Select value={form.equipment || 'none'} onValueChange={(v) => set('equipment', v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="mp-equipment"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {equipment.map((eq) => <SelectItem key={eq.equipment_number} value={eq.equipment_number}>{eq.equipment_number} — {eq.description}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Characteristic</Label><Input data-testid="mp-characteristic" value={form.characteristic} onChange={(e) => set('characteristic', e.target.value)} placeholder="Temperature, Operating Hours..." /></div>
            <div><Label>Unit</Label><Input data-testid="mp-unit" value={form.unit} onChange={(e) => set('unit', e.target.value)} placeholder="°C, h, bar..." /></div>
            <div className="col-span-2 flex items-center gap-2 rounded-lg border p-3">
              <Checkbox id="is_counter" data-testid="mp-is-counter" checked={form.is_counter} onCheckedChange={(v) => set('is_counter', !!v)} />
              <Label htmlFor="is_counter" className="cursor-pointer">This is a counter (cumulative; tracks difference between readings)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.description} data-testid="save-mp-btn">{saving ? 'Saving...' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail / readings */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">{detail.measuring_point}
                  <Badge variant={detail.is_counter ? 'default' : 'secondary'}>{detail.is_counter ? 'Counter' : 'Measuring Point'}</Badge>
                </DialogTitle>
                <DialogDescription>{detail.description} {detail.characteristic ? `· ${detail.characteristic}` : ''} {detail.unit ? `(${detail.unit})` : ''}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg bg-gray-50 border p-2"><div className="text-xs text-gray-500">Last Reading</div><div className="text-lg font-bold" data-testid="mp-last-reading">{detail.last_reading ?? '—'}</div></div>
                {detail.is_counter && <div className="rounded-lg bg-gray-50 border p-2"><div className="text-xs text-gray-500">Total Counter</div><div className="text-lg font-bold" data-testid="mp-total-counter">{detail.total_counter}</div></div>}
              </div>

              {writable && (
                <Card className="p-4 border-sky-100">
                  <h4 className="font-semibold text-gray-900 mb-2">Record Reading</h4>
                  <div className="grid grid-cols-3 gap-3 items-end">
                    <div><Label>Value</Label><Input type="number" data-testid="mp-reading-value" value={reading.reading_value} onChange={(e) => setReading((p) => ({ ...p, reading_value: e.target.value }))} /></div>
                    <div className="col-span-2"><Label>Note</Label><Input data-testid="mp-reading-note" value={reading.note} onChange={(e) => setReading((p) => ({ ...p, note: e.target.value }))} /></div>
                  </div>
                  <Button className="mt-2" size="sm" onClick={addReading} disabled={busy} data-testid="add-reading-btn">Add Reading</Button>
                </Card>
              )}

              <div>
                <h4 className="font-semibold text-sm mb-1">Reading History</h4>
                {(detail.readings || []).length === 0 ? <p className="text-xs text-gray-400">No readings yet.</p> : (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead>Date</TableHead><TableHead className="text-right">Value</TableHead>
                        {detail.is_counter && <TableHead className="text-right">Difference</TableHead>}<TableHead>By</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {[...detail.readings].reverse().map((rd, i) => (
                          <TableRow key={i} data-testid={`reading-row-${i}`}>
                            <TableCell>{rd.reading_date ? new Date(rd.reading_date).toLocaleDateString() : '—'}</TableCell>
                            <TableCell className="text-right">{rd.reading_value}</TableCell>
                            {detail.is_counter && <TableCell className="text-right">{rd.difference ?? '—'}</TableCell>}
                            <TableCell>{rd.taken_by || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default MeasuringPoints;
